import { createAsyncThunk } from '@reduxjs/toolkit';
import apiClient, { CancelToken, isCancel } from '../apiConfig';
import { IOrchestrationsServerResponse } from 'interfaces/dashboard/dashboardSlice.interface';
import { ILogFromServer } from 'interfaces/logRow.interface';
import { IOrchestrationServer } from 'interfaces/orchestration.interface';
import { IPostJobsInfo } from 'interfaces/postJobsInfo.interface';

import {
  handleThunkError,
  handleThunkErrorWithStatus,
} from 'utils/thunkErrorHandler/thunkErrorHandler';
import { formatJobsToTreeData } from 'components/TableContainer/utils/treeDataHelper';

const BASE_URL = '/csb/jobs';
let cancelTokenSources: ReturnType<typeof CancelToken.source>[] = [];

export const cancelAllOrchestrationRequests = () => {
  cancelTokenSources.forEach((source) =>
    source.cancel('Canceled due to status change')
  );
  cancelTokenSources = [];
};

export const fetchAllOrchestrations = createAsyncThunk(
  'dashboardContainer/fetchAllOrchestrations',
  async (
    data: { jobsInfo: IPostJobsInfo; isScrolling?: boolean },
    { rejectWithValue }
  ) => {
    // Source used to cancell the request when invoked
    const source = CancelToken.source();
    cancelTokenSources.push(source);
    try {
      const response = await apiClient.post<IOrchestrationsServerResponse>(
        `${BASE_URL}/hierarchical`,
        data.jobsInfo,
        {
          cancelToken: source.token,
          headers: {
            'Content-Type': 'application/json',
          },
          // Security is handled by the apiClient interceptor
        }
      );
      // Remove source when finished
      cancelTokenSources = cancelTokenSources.filter((s) => s !== source);

      // Format jobs to tree data with path strings
      const jobs = response.data.jobs || [];
      const treeDataJobs = formatJobsToTreeData(jobs);

      return {
        data: { ...response.data, jobs: treeDataJobs },
        isScrolling: data.isScrolling,
      };
    } catch (error) {
      cancelTokenSources = cancelTokenSources.filter((s) => s !== source);
      if (isCancel(error)) {
        return null;
      }
      return handleThunkError(error, rejectWithValue);
    }
  }
);

export const fetchAllOrchestrationsNoLoading = createAsyncThunk(
  'dashboardContainer/fetchAllOrchestrationsNoLoading',
  async (data: IPostJobsInfo, { rejectWithValue }) => {
    try {
      const response = await apiClient.post<IOrchestrationsServerResponse>(
        `${BASE_URL}/hierarchical`,
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      // Format jobs to tree data with path strings
      const jobs = response.data.jobs || [];
      const treeDataJobs = formatJobsToTreeData(jobs);

      return { ...response.data, jobs: treeDataJobs };
    } catch (error) {
      return handleThunkError(error, rejectWithValue);
    }
  }
);

export const getOrchestrationById = createAsyncThunk(
  'dashboardContainer/getOrchestrationById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<IOrchestrationServer>(
        `${BASE_URL}/${id}`
      );

      return response.data;
    } catch (error) {
      return handleThunkErrorWithStatus(error, rejectWithValue);
    }
  }
);

export const postViewLog = createAsyncThunk(
  'dashboardContainer/postViewLog',
  async (data: Object, { rejectWithValue }) => {
    try {
      const response = await apiClient.post<ILogFromServer[]>(
        `${BASE_URL}/log`,
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      return handleThunkError(error, rejectWithValue);
    }
  }
);
